import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

// Plan mode on the Prospecting search page: the route planner's browser half,
// kept deliberately thin. It holds the rep's draft route, posts the WHOLE draft
// to the plan preview on every change, and adopts what the server answers.
// The right column, the legend's day colours and every refusal are rendered
// server-side from Ui::* components; nothing here builds markup.
//
// Plan mode is this page with a right column added, not a second page, so the
// controller sits beside prospecting--map on the same root and only flips
// `data-plan-mode`, which the layout's group-data variants read.
//
// The draft also lives in session storage, under a key naming the tenant and
// the rep, so a search (a full page load), a reload or "Add to Favourites" never loses
// it: on load the stored draft is posted again and plan mode comes back.
// Nothing is stored server-side before "Save route", and a successful save, a
// discard or a Cancel clears it.
//
// The pin card is part of the same answer: the pin the rep last selected is
// posted beside the draft as the `selection`, and the card, its "Add to route"
// form and its refusals come back with the plan.
//
// A saved route is opened by asking the server for its plan ("Continue a
// route", or the search page arriving with `?route=` from the routes list). Its
// draft names the route and the version it was read at, and is unsaved only
// once it differs from what was opened: the opened state is kept beside the
// draft in session storage, so a reload still knows. The search page arriving
// with `?plan=start` (the list's "Plan a route") starts a new route instead.

// A map feature's kind, as the plan names the place behind it.
const PLACE_KINDS = {
  saved: "saved_site",
  candidate: "candidate",
  known: "known",
}

// An opened route's draft as a comparable string: two drafts that would save
// the same route read the same, whatever order their stops were added in.
const canonical = (draft) =>
  JSON.stringify([
    draft.route_id,
    draft.lock_version,
    draft.name,
    draft.first_day,
    draft.last_day,
    draft.time_zone,
    (draft.stops || [])
      .map((stop) => [
        stop.place_kind,
        stop.place_id,
        stop.day,
        stop.start,
        stop.end,
      ])
      .map((stop) => JSON.stringify(stop))
      .sort(),
  ])

// How long a preview's answer may take to land before the next change goes
// anyway: a 200 that is not the plan's stream (an expired session's login
// page) must not hold the plan up for good.
const SETTLE_MS = 10_000

// Stands in for a booking's id in bookingUrl (Prospecting::RouteTimeline).
const BOOKING_ID_PLACEHOLDER = "__BOOKING_ID__"

export default class extends Controller {
  static values = {
    previewUrl: String,
    startModalId: String,
    editModalId: String,
    discardModalId: String,
    storageKey: String,
    openUrl: String,
    startOnLoad: Boolean,
    bookingUrl: String,
  }

  static targets = [
    "button",
    "planData",
    "emptyPlan",
    "savedSite",
    "stopsScroll",
    "unsaved",
    "errorToast",
    "bookingFrame",
  ]

  initialize() {
    this._guardLink = this._guardLink.bind(this)
    this._guardForm = this._guardForm.bind(this)
    this._forgetPage = this._forgetPage.bind(this)
  }

  connect() {
    this._draft = null
    this._selection = null
    this._queue = Promise.resolve()
    this._settle = null
    this._stopsScrollTop = 0
    this._closed = false
    this._edited = false

    this._opened = this._readStorage(this._openedKey())
    // "Plan a route" asked for a new route: one opened and left unchanged
    // has nothing to lose, so it is not brought back over the start modal.
    if (
      this._startingOnLoad() &&
      !this._dirty(this._readStorage(this.storageKeyValue))
    ) {
      this._clearStorage(this.storageKeyValue)
      this._clearStorage(this._openedKey())
      this._opened = null
    }
    const stored = this._readStorage(this.storageKeyValue)
    this._stored = stored
    this._restoring = Boolean(stored)
    if (stored) this._preview(() => stored)
    if (this.openUrlValue) this._openOnLoad()
    if (this._startingOnLoad()) this._startOnLoad()

    // Any link on the page, the app's navigation included, and the forms
    // marked as leaving it. Bubbling, so a click whose own handler already
    // kept it on the page is left alone, and before Turbo's listeners on the
    // window, which skip an event whose default is prevented.
    document.addEventListener("click", this._guardLink)
    document.addEventListener("submit", this._guardForm)
    document.addEventListener("turbo:before-cache", this._forgetPage)
  }

  disconnect() {
    document.removeEventListener("click", this._guardLink)
    document.removeEventListener("submit", this._guardForm)
    document.removeEventListener("turbo:before-cache", this._forgetPage)
  }

  // "Plan mode" is a toggle, as in the demo: pressed while plan mode is on it
  // leaves, exactly as Cancel does.
  toggle() {
    if (this.element.dataset.planMode === "on") {
      this.cancel()
    } else {
      this.open()
    }
  }

  // "Start a route" in the column, and "Plan mode" from off: plan mode on, and
  // the start modal over it. Closing the modal leaves the column saying there
  // is no route yet.
  open() {
    this._setPlanMode(true)
    this._startModal()?.open()
  }

  // The start modal's "Start route". The form is never submitted itself: its
  // fields become a fresh draft, and the preview decides whether it stands.
  start(event) {
    event.preventDefault()
    this._selection = null
    this._closed = false
    const data = new FormData(event.target)
    this._preview(() => ({
      name: data.get("name"),
      first_day: data.get("first_day"),
      last_day: data.get("last_day"),
      time_zone: data.get("time_zone"),
      stops: [],
    }))
  }

  // "Edit" in the route header: the start form again, filled with the route.
  edit() {
    this._modal(this.editModalIdValue)?.open()
  }

  // The Edit form's "Update route": the route's name, days and time zone change,
  // and its stops stay as typed, clock times and all, whatever the zone. The
  // preview decides whether the new days stand; until it adopts them the
  // modal stays open with the refusal's copy (a plan that stands replaces the
  // column the modal lives in, which closes it).
  updateRoute(event) {
    event.preventDefault()
    this._edited = true
    const data = new FormData(event.target)
    this._previewPlan(() => ({
      ...this._draft,
      name: data.get("name"),
      first_day: data.get("first_day"),
      last_day: data.get("last_day"),
      time_zone: data.get("time_zone"),
    }))
  }

  // The Edit modal closed with an edit still on it, so the edit was refused:
  // its copy sits under the header and its values in the form. Previewing
  // the route as it stands renders both afresh.
  editClosed() {
    if (!this._edited) return

    this._edited = false
    this._previewPlan(() => this._draft)
  }

  // "Open" under "Continue a route": the saved route comes into plan mode, as
  // it is saved now. A draft with changes not yet saved is never dropped
  // without asking, even one of the same route: opening it again is the way
  // out of a save refused because another tab saved the route first.
  openRoute({ params: { url } }) {
    if (!this._unsaved()) return this._load(url)

    this._startModal()?.close()
    this._askBeforeLeaving({ url })
  }

  // "Cancel" in the route header: leaves plan mode and drops the draft. With
  // changes not yet saved it asks "Discard this route?" first; with none
  // (no route started, or an opened one as it was opened) there is nothing to
  // lose, so it just leaves. An opened route's saved state is untouched either
  // way: nothing reached the server.
  cancel() {
    if (this._unsaved()) {
      this._leaving = null
      this._modal(this.discardModalIdValue)?.open()
    } else {
      this._reset()
    }
  }

  // "Save route" ended. Saved, the route is the server's now: plan mode closes
  // and the stored draft goes, so the search page does not reopen it. A
  // refusal keeps everything as it was, with its copy under the header.
  saved({ detail: { success } }) {
    if (success) this._reset()
  }

  // The discard modal's "Discard": the route goes, and the link or form that
  // asked is followed, by whatever would have followed it (Turbo or the
  // browser), or the saved route that asked is opened. Followed before the
  // column is swapped out, while it is still on the page: a stop card's
  // "Open" lives in that column.
  discard() {
    const leaving = this._leaving
    this._leaving = null
    this._modal(this.discardModalIdValue)?.close()
    this._forgetDraft()
    if (leaving instanceof HTMLFormElement) {
      leaving.requestSubmit()
    } else if (leaving instanceof Element) {
      leaving.click()
    }
    this._forgetPage()
    if (leaving && !(leaving instanceof Element)) {
      if (leaving.url) this._load(leaving.url)
      else this.open()
    }
  }

  // Every preview REPLACES this element, so it is the one place the browser
  // learns the plan. A refused draft is not adopted: the start form says why,
  // and a rep already planning keeps the route they had.
  planDataTargetConnected(element) {
    this._settled()
    if (!element.dataset.draft) return
    const restoring = this._restoring
    const opened = element.dataset.opened === "true"
    this._restoring = false
    if (element.dataset.refused === "true") {
      // The stored draft, or the saved route just opened, no longer stands
      // (its first day has passed, or another tab saved those days): it is
      // dropped, and the start form, where the refusal's copy lands, says
      // why. A route opened over another one leaves nothing of either.
      if (opened) this._reset()
      if (restoring || opened) {
        this._clearStorage(this.storageKeyValue)
        // Plan a route's question was about that draft: with it gone there
        // is nothing to ask, and the start form takes its place.
        if (this._leaving?.start) {
          this._leaving = null
          this._modal(this.discardModalIdValue)?.close()
        }
        this.open()
      }
      return
    }
    // An answer to a preview posted before the route was discarded or saved.
    if (this._closed) return

    // A plan that stands replaces the Edit modal with the route's own values.
    this._edited = false

    this._draft = JSON.parse(element.dataset.draft)
    if (opened) {
      this._opened = canonical(this._draft)
      this._writeStorage(this._openedKey(), this._opened)
    }
    this._writeStorage(this.storageKeyValue, this._draft)
    this._startModal()?.close()
    this._setPlanMode(true)
    // From here the card is docked in the column, so the floating one hides,
    // and the pin it was showing moves into the column with it.
    const docking = this.element.dataset.planRoute !== "on"
    this.element.dataset.planRoute = "on"
    const feature = docking && this._map()?.selectedFeature()
    if (feature) {
      this._selection = this._selectionFor(feature)
      this._previewPlan(() => this._draft)
    }
    // The route layer: the plan's day lines and numbered stop pins, drawn by
    // prospecting--map whatever the search shows.
    this._map()?.setRoute(
      element.dataset.map ? JSON.parse(element.dataset.map) : null,
    )
    // An opened route is framed on its stops, as a search is on its pins.
    if (opened) this._map()?.fitPoints(JSON.parse(element.dataset.points))
    this.unsavedTargets.forEach((badge) => (badge.hidden = !this._unsaved()))
  }

  // ── The pin card ─────────────────────────────────────────────────────────

  // prospecting--map selected a pin or a list row. Outside a route the
  // floating card shows it as ever.
  pinSelected({ detail: { feature } }) {
    if (!this._planning()) return

    this._selection = this._selectionFor(feature)
    this._previewPlan(() => this._draft)
  }

  // A stop's row: its pin on the map when the page has one, which comes back
  // here through pinSelected; otherwise the card alone. Either way the stop's
  // own pin on the route layer is drawn selected once the plan comes back.
  selectStop({ params: { kind, id, fly = true } }) {
    if (!this._planning()) return

    const featureId =
      kind === "saved_site" ? this._map()?.featureIdForSite(id) : null
    if (featureId) {
      this._map().select(featureId, { fly })
    } else {
      this._map()?.closeDetail()
      this._selection = { place_kind: kind, place_id: id }
      this._previewPlan(() => this._draft)
    }
  }

  // prospecting--map heard a click on a stop's pin on the route layer: it
  // selects as the stop's row does, without flying to a pin already in view.
  stopPinSelected({ detail: { kind, id } }) {
    this.selectStop({ params: { kind, id, fly: false } })
  }

  // The timeline's one event (Ui::Calendar::DayTimelineComponent): a chip
  // was clicked or entered. Its id is "<kind>:<id>" (Prospecting::RouteTimeline).
  // A stop selects as the stop's row does, and docks its card; a booking
  // opens its own detail.
  timelineSelected({ detail: { id } }) {
    const [kind, ...rest] = String(id).split(":")
    if (!kind || rest.length === 0) return

    if (kind === "booking") {
      this._openBooking(rest.join(":"))
      return
    }
    this.selectStop({ params: { kind, id: rest.join(":") } })
  }

  // The calendar's own booking detail (BookingsController#show), read-only,
  // in the page's booking_modal frame. The same booking again reloads the
  // frame, whose modal was closed in place.
  _openBooking(id) {
    if (!this.hasBookingFrameTarget || !this.bookingUrlValue) return

    const frame = this.bookingFrameTarget
    const url = new URL(
      this.bookingUrlValue.replace(
        BOOKING_ID_PLACEHOLDER,
        encodeURIComponent(id),
      ),
      window.location.href,
    ).toString()
    if (frame.src === url) frame.reload?.()
    else frame.src = url
  }

  // "Change time" on a stop's row: its card, as the row opens it, with the
  // day and times form already open on the stop. The row's select queues the
  // preview, which reads the selection only when its turn comes, so the form
  // rides on that one preview.
  changeTime({ params: { kind, id } }) {
    if (!this._planning()) return

    this.selectStop({ params: { kind, id } })
    this._selection = { ...this._selection, form: true, day: null }
  }

  closeCard() {
    this._map()?.closeDetail()
    this._selection = null
    this._previewPlan(() => this._draft)
  }

  showForm() {
    this._withSelection({ form: true })
  }

  hideForm() {
    this._withSelection({ form: false, day: null })
  }

  // The form's day: the server pre-fills the times for it.
  dayChanged(event) {
    this._withSelection({ form: true, day: event.target.value })
  }

  // "Add stop" (or "Update stop", the same form on a stop): the stop joins
  // the draft, in place of itself when it was one, and the preview decides.
  // A refusal leaves the draft as it was and answers in the card, form still
  // open; the stop refused is then no longer in the plan, so the selection
  // carries that the form was changing it.
  addStop(event) {
    event.preventDefault()
    if (!this._planning()) return

    const data = new FormData(event.target)
    const stop = {
      place_kind: data.get("place_kind"),
      place_id: data.get("place_id"),
      day: data.get("day"),
      start: data.get("start"),
      end: data.get("end"),
    }
    this._selection = {
      ...this._selection,
      form: false,
      day: stop.day,
      change: data.get("change") === "true",
    }
    this._previewPlan(() => ({
      ...this._draft,
      stops: [...this._others(stop), stop],
    }))
  }

  // The trash icon on a row, or "Remove" on a stop's card.
  removeStop({ params: { kind, id } }) {
    if (!this._planning()) return

    this._previewPlan(() => ({
      ...this._draft,
      stops: this._others({ place_kind: kind, place_id: id }),
    }))
  }

  // The stops list a preview just rendered, scrolled to where the rep left
  // the one it replaced, then just far enough that the selected row shows:
  // a pin clicked on the map selects a row the list may have scrolled past,
  // and the card that opens for it shortens the list.
  stopsScrollTargetConnected(element) {
    element.scrollTop = this._stopsScrollTop
    this._revealSelectedStop(element)
  }

  // Measured, not scrollIntoView: that would also scroll the page, and it
  // does not know the sticky day header covers the list's top edge.
  _revealSelectedStop(list) {
    const row = list.querySelector("[data-selected]")
    if (!row) return
    const view = list.getBoundingClientRect()
    const box = row.getBoundingClientRect()
    const header = row.closest("section")?.querySelector("header")
    const top = view.top + (header ? header.getBoundingClientRect().height : 0)
    if (box.top < top) {
      list.scrollTop -= top - box.top
    } else if (box.bottom > view.bottom) {
      list.scrollTop += Math.min(box.bottom - view.bottom, box.top - top)
    }
  }

  // "Add to Favourites" on a candidate's card answered with the site it saved: the
  // card opens on the saved site. The pin turns saved from the same answer's
  // marker (prospecting--map#siteMarkerTargetConnected).
  savedSiteTargetConnected(element) {
    const { featureId, siteId } = element.dataset
    if (!siteId) return
    if (!this._planning()) return

    this._selection = {
      place_kind: "saved_site",
      place_id: siteId,
      feature_id: featureId || null,
    }
    this._previewPlan(() => this._draft)
  }

  // Nothing was stored, so there is nothing to undo: the draft goes, from
  // memory and session storage, and the page goes back to plan mode off.
  _reset() {
    this._forgetDraft()
    this._forgetPage()
  }

  _forgetDraft() {
    this._closed = true
    this._restoring = false
    this._draft = null
    this._opened = null
    this._selection = null
    this._clearStorage(this.storageKeyValue)
    this._clearStorage(this._openedKey())
  }

  // The page as the server renders it: plan mode off, no route on the map, and
  // the column and the legend's day list in their no-route state, swapped in from the
  // server-rendered copy, so the next "Plan mode" starts clean. Also run just
  // before Turbo caches the page: a cached copy carrying the last answer's
  // draft would adopt that route again on a Back, even after it was saved or
  // discarded. A route still being planned comes back from session storage.
  _forgetPage() {
    delete this.element.dataset.planRoute
    this._setPlanMode(false)
    this._map()?.setRoute(null)
    this.planDataTargets.forEach((element) => delete element.dataset.draft)
    if (!this.hasEmptyPlanTarget) return

    for (const fresh of this.emptyPlanTarget.content.children) {
      document.getElementById(fresh.id)?.replaceWith(fresh.cloneNode(true))
    }
  }

  // A link that leaves the search page with an unsaved route asks first. A
  // reload is not leaving: the stored draft simply comes back, so there is
  // no `beforeunload` prompt. Nor is a link that opens elsewhere (a new tab,
  // a download), or one back to this page: a fragment, or a search, which
  // keeps the draft like any other. Nor is a link that only swaps a Turbo
  // frame on this page ("Create qualified lead" on a route card).
  _guardLink(event) {
    if (!this._unsaved() || event.defaultPrevented) return
    if (event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const link = event.target.closest?.("a[href]")
    if (!link || link.hasAttribute("download")) return
    const target = link.getAttribute("target")
    if (target && !["_self", "_top"].includes(target)) return
    if (target !== "_top" && this._framed(link)) return
    const url = new URL(link.href, window.location.href)
    if (!url.protocol.startsWith("http")) return
    if (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname
    )
      return

    event.preventDefault()
    this._askBeforeLeaving(link)
  }

  // Whether Turbo would load `link` into a frame rather than the page:
  // `data-turbo-frame` names one, or the link sits in a frame that does not
  // target `_top`.
  _framed(link) {
    if (link.closest('[data-turbo="false"]')) return false
    const named = link.dataset.turboFrame
    if (named) return named !== "_top"
    const frame = link.closest("turbo-frame")
    return !!frame && frame.getAttribute("target") !== "_top"
  }

  // Most forms here stay on the page ("Add to Favourites", a search); the ones that
  // navigate away (a known account's "Open") say so with `data-leaves-page`.
  _guardForm(event) {
    if (!this._unsaved() || event.defaultPrevented) return
    if (!event.target.matches?.("form[data-leaves-page]")) return

    event.preventDefault()
    this._askBeforeLeaving(event.target)
  }

  _askBeforeLeaving(leaving) {
    this._leaving = leaving
    this._modal(this.discardModalIdValue)?.open()
  }

  // A new route being planned is unsaved until "Save route": nothing reaches
  // the server before it. An opened one is unsaved once it differs from what
  // was opened. So is a stored one still on its way back after a load.
  _unsaved() {
    return this._dirty(this._current())
  }

  _current() {
    return this._draft ?? (this._restoring ? this._stored : null)
  }

  _dirty(draft) {
    if (!draft) return false
    return !draft.route_id || canonical(draft) !== this._opened
  }

  // The search page arrived from the routes list's "Open". The route is opened
  // once a stored draft is back, as "Continue a route" would open it: asking
  // first when that draft has changes not yet saved. The route leaves the
  // address, so a reload or Back does not open it over later work.
  _openOnLoad() {
    const url = new URL(window.location.href)
    if (url.searchParams.has("route")) {
      url.searchParams.delete("route")
      window.history.replaceState(window.history.state, "", url)
    }
    const params = { url: this.openUrlValue }
    this._then(() => this.openRoute({ params }))
  }

  // The search page arrived from the routes list's "Plan a route", with the
  // start modal open. A draft with changes not yet saved is still never
  // dropped without asking: it comes back behind the question, and Discard
  // starts the new route. Asked at once rather than once the draft is back,
  // which can take seconds with the modal open over it. The server rendered
  // the start modal open; it is shut here, before its controller (later in
  // the page) connects, so it neither stays nor fades out under the
  // question, which waits a microtask for the modals' controllers. The
  // request leaves the address, as a route opened by "Open" does.
  _startOnLoad() {
    const url = new URL(window.location.href)
    if (url.searchParams.has("plan")) {
      url.searchParams.delete("plan")
      window.history.replaceState(window.history.state, "", url)
    }
    if (!this._unsaved()) return

    const startModal = document.getElementById(this.startModalIdValue)
    startModal?.setAttribute("data-ui--modal-open-value", "false")
    startModal?.removeAttribute("open")
    queueMicrotask(() => {
      const modal = this._startModal()
      if (modal?.element.open) modal.close()
      this._askBeforeLeaving({ start: true })
    })
  }

  // A route named by "Open" wins: no link names both.
  _startingOnLoad() {
    return this.startOnLoadValue && !this.openUrlValue
  }

  _planning() {
    return this.element.dataset.planMode === "on" && this._draft !== null
  }

  _withSelection(changes) {
    if (!this._planning() || !this._selection) return

    this._selection = { ...this._selection, ...changes }
    this._previewPlan(() => this._draft)
  }

  _others(place) {
    return (this._draft.stops || []).filter(
      (stop) =>
        !(
          stop.place_kind === place.place_kind &&
          stop.place_id === place.place_id
        ),
    )
  }

  // What the card needs of a pin. A saved site is only named — the server
  // reads it again; a candidate or a known account sends what its pin shows.
  _selectionFor(feature) {
    const kind = PLACE_KINDS[feature.kind] || "candidate"
    const placeId = {
      saved_site: feature.site_id,
      known: feature.account_id,
      candidate: feature.id,
    }[kind]
    return {
      place_kind: kind,
      place_id: placeId,
      feature_id: feature.id,
      name: feature.name,
      address: feature.address,
      employees: feature.employees,
      sector: feature.sector,
      source: feature.source,
      status: feature.status,
      space_branch_key: feature.space_branch_key,
      candidate: feature.candidate,
    }
  }

  _setPlanMode(on) {
    this.element.dataset.planMode = on ? "on" : "off"
    this.buttonTargets.forEach((button) => {
      button.dataset.active = String(on)
      button.setAttribute("aria-pressed", String(on))
    })
  }

  // One preview at a time, each built from the draft the last answer left.
  // Turbo renders a stream after the next repaint, so a change made while an
  // answer is on its way would otherwise post the draft that answer is about
  // to replace, and undo it: trash two stops quickly and the first comes back.
  _preview(build) {
    this._then(() => {
      const draft = build()
      return draft && this._send(draft)
    })
  }

  // A saved route's plan, in the same queue: a change made while it is on its
  // way applies to the route it brings.
  _load(url) {
    this._then(() => {
      this._closed = false
      this._selection = null
      return this._answer(() => get(url, { responseKind: "turbo-stream" }))
    })
  }

  _then(step) {
    this._queue = this._queue.then(step).catch((error) => {
      console.error(error)
      this._failed()
    })
  }

  // A change to the route being planned. Built when its turn comes, so one
  // queued behind Cancel posts nothing.
  _previewPlan(build) {
    this._preview(() => (this._planning() ? build() : null))
  }

  // A form field, never a JSON body: Rodauth reads `application/json` as a
  // JWT API call and answers 401 to the session cookie. Every 200 replaces
  // `route_plan_data`, which settles the turn; anything else settles it here.
  _send(draft) {
    this._stopsScrollTop = this.hasStopsScrollTarget
      ? this.stopsScrollTarget.scrollTop
      : 0
    const body = new FormData()
    body.append("draft", JSON.stringify(draft))
    if (this._selection)
      body.append("selection", JSON.stringify(this._selection))
    return this._answer(() =>
      post(this.previewUrlValue, { body, responseKind: "turbo-stream" }),
    )
  }

  async _answer(request) {
    const settled = new Promise((resolve) => (this._settle = resolve))
    const response = await request()
    if (!response?.ok) {
      // A 422 carries its own refusal as streams; anything else (a 403, a
      // 429, a 500) would otherwise leave the plan unchanged with no word.
      if (!response?.unprocessableEntity) this._failed()
      return this._settled()
    }

    await Promise.race([settled, new Promise((r) => setTimeout(r, SETTLE_MS))])
    this._settle = null
  }

  // The toast is rendered by the view, so its copy stays in Rails i18n;
  // cloning it into #toast-container lets ui--toast animate and dismiss it.
  _failed() {
    const container = document.getElementById("toast-container")
    if (!container || !this.hasErrorToastTarget) return

    const toast =
      this.errorToastTarget.content.firstElementChild.cloneNode(true)
    toast.id = `toast-route-plan-${Math.random().toString(36).slice(2, 10)}`
    container.appendChild(toast)
  }

  _settled() {
    this._settle?.()
    this._settle = null
  }

  _map() {
    return this.application.getControllerForElementAndIdentifier(
      this.element,
      "prospecting--map",
    )
  }

  // Session storage can be missing or throw (a private window, blocked site
  // data): planning then simply lasts as long as the page.
  _readStorage(key) {
    if (!key) return null
    try {
      return JSON.parse(window.sessionStorage.getItem(key))
    } catch {
      return null
    }
  }

  _writeStorage(key, value) {
    if (!key) return
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Planning goes on without it.
    }
  }

  _clearStorage(key) {
    if (!key) return
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      // Nothing was stored.
    }
  }

  // The opened route as it was opened, beside the draft.
  _openedKey() {
    return this.storageKeyValue && `${this.storageKeyValue}:opened`
  }

  _startModal() {
    return this._modal(this.startModalIdValue)
  }

  _modal(id) {
    const element = id && document.getElementById(id)
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(
      element,
      "ui--modal",
    )
  }
}
